import {
  TovButton,
  Input,
  msg,
  TovCard,
  TovIcon,
  TovShimmer,
  PageLayout,
  buildOpeningExperiencePreviewData,
  InvitationSurface,
  useToast,
  useTovForm,
  useTovFormStore,
} from '@tov-plus/ui';
import { useEffect, useState } from 'react';
import { useParams } from '@tov-plus/frontend-data';
import { useTovQuery } from '@tov-plus/frontend-data';

import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { PublishedInvitationSurface } from './rsvp/RsvpPage';

const publicInviteErrorMessage = () =>
  msg(
    'app.access.this_invite_link_is_invalid_expired_or_no_longer_allowed_for_this',
  );

type BrowserJoinedPerson = {
  id: string;
  displayName: string;
  joinedAt: string;
};

type BrowserPublicInviteRoster = {
  deliveryToken: string | null;
  people: BrowserJoinedPerson[];
};

type PublicInvitePersonFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const emptyBrowserRoster: BrowserPublicInviteRoster = {
  deliveryToken: null,
  people: [],
};

function createPublicInvitePersonFormState(): PublicInvitePersonFormState {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  };
}

function getBrowserRosterKey(identifier: string) {
  return `tov-plus:public-invite-roster:${identifier}`;
}

function readBrowserRoster(identifier: string | null | undefined) {
  if (!identifier || typeof window === 'undefined') {
    return emptyBrowserRoster;
  }

  try {
    const storedValue = window.localStorage.getItem(
      getBrowserRosterKey(identifier),
    );

    if (!storedValue) {
      return emptyBrowserRoster;
    }

    const parsed = JSON.parse(
      storedValue,
    ) as Partial<BrowserPublicInviteRoster>;

    return {
      deliveryToken:
        typeof parsed.deliveryToken === 'string' ? parsed.deliveryToken : null,
      people: Array.isArray(parsed.people)
        ? parsed.people.filter(
            (person): person is BrowserJoinedPerson =>
              typeof person?.id === 'string' &&
              typeof person.displayName === 'string' &&
              typeof person.joinedAt === 'string',
          )
        : [],
    };
  } catch {
    return emptyBrowserRoster;
  }
}

function writeBrowserRoster(
  identifiers: Array<string | null | undefined>,
  roster: BrowserPublicInviteRoster,
) {
  if (typeof window === 'undefined') {
    return;
  }

  const serialized = JSON.stringify(roster);

  identifiers.forEach((identifier) => {
    if (!identifier) {
      return;
    }

    try {
      window.localStorage.setItem(getBrowserRosterKey(identifier), serialized);
    } catch {
      // Browser memory is a convenience; server-side join/add-person state is authoritative.
    }
  });
}

function addBrowserRosterPerson(
  roster: BrowserPublicInviteRoster,
  person: BrowserJoinedPerson,
): BrowserPublicInviteRoster {
  const existingIndex = roster.people.findIndex(
    (item) => item.id === person.id,
  );

  if (existingIndex >= 0) {
    const people = [...roster.people];
    people[existingIndex] = person;
    return {
      ...roster,
      people,
    };
  }

  return {
    ...roster,
    people: [...roster.people, person],
  };
}

function hasContactMethod(input: { email: string; phone: string }) {
  return input.email.trim().length > 0 || input.phone.trim().length > 0;
}

function formatPublicInviteDate(value: string | null) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function PublicInviteJoinPage() {
  const { token, deliveryToken: routeDeliveryToken } = useParams();
  const notifications = useToast();
  const [status, setStatus] = useState<
    'loading' | 'ready' | 'joined' | 'error'
  >('loading');
  const [deliveryToken, setDeliveryToken] = useState<string | null>(null);
  const [browserRoster, setBrowserRoster] =
    useState<BrowserPublicInviteRoster>(emptyBrowserRoster);
  const joinForm = useTovForm({
    defaultValues: createPublicInvitePersonFormState(),
    onSubmit: async ({ value }) => {
      if (!token) {
        return;
      }

      if (!hasContactMethod(value)) {
        notifications.error(
          msg('pages.public_invite_join.you_need_at_least_one_contact_detail'),
        );
        return;
      }

      try {
        const result = await guestApi.joinPublicInvitation(token, {
          firstName: value.firstName,
          lastName: value.lastName,
          email: value.email || null,
          phone: value.phone || null,
        });
        setDeliveryToken(result.delivery.public_token);
        const nextRoster = addBrowserRosterPerson(
          {
            deliveryToken: result.delivery.public_token,
            people: [],
          },
          {
            id: result.contact.id,
            displayName: result.contact.display_name,
            joinedAt: new Date().toISOString(),
          },
        );
        setBrowserRoster(nextRoster);
        writeBrowserRoster([token, result.delivery.public_token], nextRoster);
        setStatus('joined');
        notifications.success(msg('pages.public_invite_join.joined'));
      } catch (error) {
        notifications.error(
          getGuestErrorMessage(error, publicInviteErrorMessage()),
        );
        throw error;
      }
    },
  });
  const addPersonForm = useTovForm({
    defaultValues: createPublicInvitePersonFormState(),
    onSubmit: async ({ value }) => {
      if (!deliveryToken) {
        return;
      }
      try {
        const result = await guestApi.addPersonFromDelivery(deliveryToken, {
          firstName: value.firstName,
          lastName: value.lastName,
          email: value.email || null,
          phone: value.phone || null,
        });
        const persistedRoster = {
          ...addBrowserRosterPerson(browserRoster, {
            id: result.contact.id,
            displayName: result.contact.display_name,
            joinedAt: new Date().toISOString(),
          }),
          deliveryToken,
        };
        setBrowserRoster(persistedRoster);
        writeBrowserRoster([token, deliveryToken], persistedRoster);
        addPersonForm.reset();
        notifications.success(
          msg(
            'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_guest_added_to_this_invitation',
          ),
        );
      } catch (error) {
        notifications.error(
          getGuestErrorMessage(error, publicInviteErrorMessage()),
        );
        throw error;
      }
    },
  });
  const form = useTovFormStore(joinForm.store, (state) => state.values);
  const addedPerson = useTovFormStore(
    addPersonForm.store,
    (state) => state.values,
  );
  const joinSubmitting = useTovFormStore(
    joinForm.store,
    (state) => state.isSubmitting,
  );
  const addPersonSubmitting = useTovFormStore(
    addPersonForm.store,
    (state) => state.isSubmitting,
  );

  const inviteQuery = useTovQuery({
    queryKey: routeDeliveryToken
      ? guestQueryKeys.publicDeliveryInvite(routeDeliveryToken)
      : token
        ? guestQueryKeys.publicInvite(token)
        : guestQueryKeys.publicInvite('pending'),
    queryFn: () =>
      routeDeliveryToken
        ? guestApi.getPublicDeliveryInvitation(routeDeliveryToken)
        : guestApi.getPublicInvitation(token!),
    enabled: Boolean(token || routeDeliveryToken),
  });
  const invite = inviteQuery.data ?? null;

  useEffect(() => {
    if (!token && !routeDeliveryToken) {
      setStatus('error');
      return;
    }
    if (!invite) {
      setStatus(inviteQuery.isError ? 'error' : 'loading');
      return;
    }
    if (routeDeliveryToken) {
      const deliveryInvite = invite as {
        delivery?: { public_token?: string | null } | null;
      };
      const publicDeliveryToken =
        deliveryInvite.delivery?.public_token ?? routeDeliveryToken;
      setDeliveryToken(publicDeliveryToken);
      setBrowserRoster(readBrowserRoster(routeDeliveryToken));
      setStatus('joined');
      return;
    }

    const storedRoster = readBrowserRoster(token);
    setBrowserRoster(storedRoster);
    setDeliveryToken(storedRoster.deliveryToken);
    setStatus(storedRoster.deliveryToken ? 'joined' : 'ready');
  }, [invite, inviteQuery.isError, routeDeliveryToken, token]);

  if ((!token && !routeDeliveryToken) || status === 'error') {
    return (
      <main className="guest-standalone">
        <PageLayout
          title={msg('pages.public_invite_join.join_with_this_invitation')}
        >
          <StatePanel preset="access-unavailable" />
        </PageLayout>
      </main>
    );
  }

  if (status === 'loading' || !invite) {
    return (
      <main className="guest-standalone" data-testid="public-invite-join-page">
        <PageLayout
          className="public-invite-join-page"
          title={msg('pages.public_invite_join.join_with_this_invitation')}
        >
          <TovCard variant="content" isWide>
            <TovShimmer
              className="public-invite-join-page__invitation-shimmer"
              height="clamp(16rem, 42vh, 28rem)"
            />
          </TovCard>
        </PageLayout>
      </main>
    );
  }

  const primaryEvent = invite.events[0] ?? null;
  const invitationData = buildOpeningExperiencePreviewData({
    title: invite.invitation.guest_display_name || invite.occasion.display_name,
    dateLabel:
      formatPublicInviteDate(primaryEvent?.starts_at ?? null) ??
      invite.occasion.display_name,
    locationLabel: primaryEvent?.display_name ?? invite.occasion.display_name,
    themeLabel: invite.occasion.display_name,
  });
  const eventCount = invite.events.length;
  const pageTitle =
    status === 'joined'
      ? msg('pages.public_invite_join.joined')
      : msg('pages.public_invite_join.join_with_this_invitation');

  return (
    <main className="guest-standalone" data-testid="public-invite-join-page">
      <PageLayout className="public-invite-join-page" title={pageTitle}>
        <section className="public-invite-join-page__layout">
          <section className="public-invite-join-page__invitation">
            {invite.publishableInvitation ? (
              <PublishedInvitationSurface
                document={invite.publishableInvitation.document}
                imageUrlByAssetId={
                  invite.publishableInvitation.imageUrlByAssetId
                }
                fallback={<InvitationSurface invitationData={invitationData} />}
              />
            ) : (
              <InvitationSurface invitationData={invitationData} />
            )}
          </section>

          <TovCard
            variant="content"
            as="form"
            onSubmit={async (event) => {
              event.preventDefault();
              if (status === 'joined') {
                await addPersonForm.handleSubmit();
                return;
              }

              await joinForm.handleSubmit();
            }}
            className={`public-invite-join-page__form guest-rsvp-form${
              status === 'joined'
                ? ' public-invite-join-page__form--joined'
                : ''
            }`}
          >
            <div className="public-invite-join-page__form-heading">
              <span className="public-invite-join-page__form-icon">
                <TovIcon name={status === 'joined' ? 'check' : 'users'} />
              </span>
              <div>
                <h2>
                  {status === 'joined'
                    ? msg('pages.public_invite_join.joined')
                    : msg('pages.public_invite_join.join_with_this_invitation')}
                </h2>
                <p>{invite.occasion.display_name}</p>
              </div>
            </div>

            <div className="public-invite-join-page__event-list">
              {invite.events.map((event) => (
                <article
                  className="public-invite-join-page__event"
                  key={event.id}
                >
                  <TovIcon name="calendar-blank" />
                  <div>
                    <strong>{event.display_name}</strong>
                    <span>
                      {formatPublicInviteDate(event.starts_at) ??
                        invite.occasion.display_name}
                    </span>
                  </div>
                </article>
              ))}
            </div>

            {status !== 'joined' && token ? (
              <>
                <div className="public-invite-join-page__fields">
                  <label className="field-stack">
                    <span>{msg('general.fields.first_name.label')}</span>
                    <Input
                      onChange={(event) =>
                        joinForm.setFieldValue('firstName', event.target.value)
                      }
                      required
                      value={form.firstName}
                    />
                  </label>
                  <label className="field-stack">
                    <span>{msg('general.fields.last_name.label')}</span>
                    <Input
                      onChange={(event) =>
                        joinForm.setFieldValue('lastName', event.target.value)
                      }
                      required
                      value={form.lastName}
                    />
                  </label>
                  <label className="field-stack">
                    <span>{msg('general.fields.email.label')}</span>
                    <Input
                      onChange={(event) =>
                        joinForm.setFieldValue('email', event.target.value)
                      }
                      type="email"
                      value={form.email}
                    />
                  </label>
                  <label className="field-stack">
                    <span>{msg('general.fields.phone.label')}</span>
                    <Input
                      onChange={(event) =>
                        joinForm.setFieldValue('phone', event.target.value)
                      }
                      value={form.phone}
                    />
                  </label>
                </div>
                <TovButton disabled={joinSubmitting} type="submit">
                  {msg('pages.public_invite_join.join')}
                </TovButton>
              </>
            ) : null}

            {status === 'joined' && browserRoster.people.length > 0 ? (
              <section
                className="public-invite-join-page__joined-list"
                aria-label={msg(
                  'pages.public_invite_join.joined_from_this_browser',
                )}
              >
                <div className="public-invite-join-page__joined-list-heading">
                  <TovIcon name="check" />
                  <strong>
                    {msg('pages.public_invite_join.joined_from_this_browser')}
                  </strong>
                </div>
                <ul>
                  {browserRoster.people.map((person) => (
                    <li key={person.id}>
                      <span>{person.displayName}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {status === 'joined' && invite.allow_add_others && deliveryToken ? (
              <section className="guest-rsvp-add-person-panel public-invite-join-page__add-person">
                <div className="guest-rsvp-add-person-panel__trigger">
                  <span className="guest-rsvp-add-person-panel__icon">
                    <TovIcon name="plus" />
                  </span>
                  <span>
                    <strong>
                      {msg('pages.public_invite_join.add_another_guest')}
                    </strong>
                    <small>
                      {eventCount} {msg('general.modules.events.name')}
                    </small>
                  </span>
                </div>

                <div className="guest-rsvp-add-person-panel__fields">
                  <label className="field-stack">
                    <span>{msg('general.fields.first_name.label')}</span>
                    <Input
                      onChange={(event) =>
                        addPersonForm.setFieldValue(
                          'firstName',
                          event.target.value,
                        )
                      }
                      required
                      value={addedPerson.firstName}
                    />
                  </label>
                  <label className="field-stack">
                    <span>{msg('general.fields.last_name.label')}</span>
                    <Input
                      onChange={(event) =>
                        addPersonForm.setFieldValue(
                          'lastName',
                          event.target.value,
                        )
                      }
                      required
                      value={addedPerson.lastName}
                    />
                  </label>
                  <label className="field-stack">
                    <span>{msg('general.fields.email.label')}</span>
                    <Input
                      onChange={(event) =>
                        addPersonForm.setFieldValue('email', event.target.value)
                      }
                      type="email"
                      value={addedPerson.email}
                    />
                  </label>
                  <label className="field-stack">
                    <span>{msg('general.fields.phone.label')}</span>
                    <Input
                      onChange={(event) =>
                        addPersonForm.setFieldValue('phone', event.target.value)
                      }
                      value={addedPerson.phone}
                    />
                  </label>
                  <TovButton disabled={addPersonSubmitting} type="submit">
                    {msg('pages.public_invite_join.add_person')}
                  </TovButton>
                </div>
              </section>
            ) : null}
          </TovCard>
        </section>
      </PageLayout>
    </main>
  );
}
